import {
  poseDateSchema,
  saveWorkoutPoseRoutineSchema,
  workoutPoses,
} from "@/api/db/schema/workout-poses";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

export const saveWorkoutPoseRoutineFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(saveWorkoutPoseRoutineSchema)
  .handler(async ({ context, data }) => {
    const { workoutPoseService } = await import(
      "@/api/services/workout-pose.service"
    );
    const isAdmin = context.auth.role === "admin";
    return workoutPoseService.saveWorkoutPoseRoutine(
      context.auth.userId,
      data,
      isAdmin,
    );
  });

export const getWorkoutPoseRoutineFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ date: poseDateSchema }))
  .handler(async ({ context, data }) => {
    const { workoutPoseService } = await import(
      "@/api/services/workout-pose.service"
    );
    return workoutPoseService.getWorkoutPoseRoutine(
      context.auth.userId,
      data.date,
    );
  });

export const getWorkoutPoseRoutineOptions = (date: string) =>
  queryOptions({
    queryKey: ["workoutPoseRoutine", date],
    queryFn: () => getWorkoutPoseRoutineFn({ data: { date } }),
  });

export const uploadPosePhotoFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(
    z.object({
      contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
      size: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024),
    }),
  )
  .handler(async ({ context, data }) => {
    const { posePhotoService } = await import(
      "@/api/services/pose-photo.service"
    );
    return posePhotoService.createUpload(
      context.auth.userId,
      data.contentType,
      data.size,
    );
  });

export const getPosePhotoFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ key: z.string().max(300) }))
  .handler(async ({ context, data }) => {
    const { posePhotoService, ownsPosePhoto } = await import(
      "@/api/services/pose-photo.service"
    );
    const { db } = await import("@/api/db/db.index");
    const { sql } = await import("drizzle-orm");

    if (
      context.auth.role !== "admin" &&
      !ownsPosePhoto(data.key, context.auth.userId)
    ) {
      throw new Error("Bu fotoğrafa erişim yetkiniz yok.");
    }

    const [pose] = await db
      .select({ id: workoutPoses.poseRoutineId })
      .from(workoutPoses)
      .where(sql`instr(${workoutPoses.data}, ${data.key}) > 0`)
      .limit(1);

    if (!pose && !ownsPosePhoto(data.key, context.auth.userId)) {
      throw new Error("Fotoğraf kaydı bulunamadı.");
    }

    return { url: await posePhotoService.getPhotoUrl(data.key) };
  });

export const getClientWorkoutPosesFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ clientId: z.string().min(1) }))
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Bu danışan verilerine erişim yetkiniz yok.");
    }
    const { workoutPoseService } = await import(
      "@/api/services/workout-pose.service"
    );
    return workoutPoseService.getClientWorkoutPoses(data.clientId);
  });

export const getClientWorkoutPosesOptions = (clientId: string) =>
  queryOptions({
    queryKey: ["clientWorkoutPoses", clientId],
    queryFn: () => getClientWorkoutPosesFn({ data: { clientId } }),
  });
